import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ScanText, Upload, Camera, Loader2, ChevronDown, ChevronRight,
  Check, X, Trash2, AlertTriangle, Sparkles, Save, RefreshCw,
} from "lucide-react";
import api from "../lib/api";
import DateTimePicker from "../components/DateTimePicker";
import ConfirmModal from "../components/ConfirmModal";
import SearchableSelect from "../components/SearchableSelect";

/* Stage 4C — Invoice OCR review queue.
 * Pipeline: snap photo → backend GPT-5.2 Vision → editable line items → match
 * against inventory → "Apply" creates equipment + seeds service schedules.
 * Admin/manager/owner only — backend enforces. */
export default function OcrInvoicesPage() {
  const [params, setParams] = useSearchParams();
  const presetCustomer = params.get("customer_id") || "";
  const presetSite = params.get("site_id") || "";
  const presetJob = params.get("job") || "";
  const presetSalesDoc = params.get("sales_doc") || "";
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("pending_review");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Iter 398c — when Dirk deep-links from an invoice row, we scope the
  // job list to that customer AND auto-flip tabs to the bucket the job
  // is actually in (was previously always defaulting to
  // 'pending_review', which hid 'processing' + 'failed' rows so the
  // user reported "OCR complete but not in the scanner list").
  const [customerScope, setCustomerScope] = useState(!!presetCustomer);

  const load = async () => {
    const [j, c, s] = await Promise.all([
      api.get("/ocr/invoice"),
      api.get("/customers"),
      api.get("/sites"),
    ]);
    setJobs(j.data);
    setCustomers(c.data);
    setSites(s.data);
  };
  useEffect(() => {
    load();
  }, []);

  // Iter 398c — when a specific ?job=… or ?sales_doc=… is deep-linked,
  // find it (across ALL statuses), auto-switch to the correct tab, and
  // auto-expand it. Also honour customer scope by default.
  const [salesDocOrphan, setSalesDocOrphan] = useState(false);
  useEffect(() => {
    if (!jobs.length) { setSalesDocOrphan(false); return; }
    let target = null;
    if (presetJob) target = jobs.find((j) => j.id === presetJob);
    else if (presetSalesDoc) target = jobs.find((j) => j.source_sales_document_id === presetSalesDoc);
    // Iter 411 — Orphan-sales-doc guard. When ?sales_doc=… but no OCR
    // job record exists (Dirk's SAL2888.11 case: retention pass cleaned
    // the ocr_documents row while line_items survived on the sales doc),
    // we surface a banner offering a fresh re-scan instead of silently
    // showing the customer's full job list with nothing highlighted.
    if (!target && presetSalesDoc) {
      setSalesDocOrphan(true);
      return;
    }
    setSalesDocOrphan(false);
    if (!target) return;
    // Move the tab to the bucket that contains this job so the user
    // can actually see it appear (highlighted below).
    const status = target.status || "pending_review";
    setTab(status);
    setOpenId(target.id);
    // Scroll the row into view once React has painted.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="ocr-row-${target.id}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
  }, [jobs, presetJob, presetSalesDoc]);

  // Iter 411 — Trigger a fresh OCR scan on the orphaned sales doc so
  // a new ocr_documents record materialises (and the operator can
  // start editing lines / matching inventory once it completes).
  const [rescanBusy, setRescanBusy] = useState(false);
  const requestOrphanRescan = async () => {
    if (!presetSalesDoc || rescanBusy) return;
    setRescanBusy(true);
    try {
      await api.post(`/sales-documents/${presetSalesDoc}/rescan-ocr`);
      // Poll for the fresh job every 3s (max 3 min).
      for (let i = 0; i < 60; i += 1) {
        await new Promise((r) => setTimeout(r, 3000));
        await load();
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not re-scan this sales document.");
    } finally {
      setRescanBusy(false);
    }
  };

  const filtered = jobs.filter((j) => {
    if (customerScope && presetCustomer && j.customer_id !== presetCustomer) return false;
    if (tab === "all") return true;
    return (j.status || "pending_review") === tab;
  });

  const fileRef = useRef(null);
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const { data } = await api.post("/ocr/invoice", {
            image_base64: reader.result,
            mime_type: file.type || "image/jpeg",
            customer_id: presetCustomer || null,
            site_id: presetSite || null,
          });
          await load();
          setOpenId(data.id);
        } catch (ex) {
          setErr(ex?.response?.data?.detail || "OCR failed");
        } finally {
          setBusy(false);
          e.target.value = "";
        }
      };
      reader.readAsDataURL(file);
    } catch (ex) {
      setErr(String(ex));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="ocr-invoices-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">
            Stage 4C · GPT-5.2 vision
          </div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <ScanText className="w-7 h-7 text-cyan-400" />
            Invoice OCR
          </h1>
          <p className="text-xs text-[#647087] mt-1 max-w-xl">
            Snap a supplier invoice or quote (image or PDF) — AI extracts line items, matches them
            to your inventory, and (after your review) creates equipment + seeds service schedules in one click.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={onUpload}
            className="hidden"
            data-testid="ocr-file-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="hv-btn hv-btn-primary"
            data-testid="ocr-upload-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {busy ? "Reading invoice…" : "Scan invoice"}
          </button>
        </div>
      </div>

      {err && (
        <div className="hv-card p-3 border border-rose-500/40 bg-rose-500/10 text-sm text-rose-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      )}

      {/* Iter 411 — Orphan-sales-doc banner. Fires when the deep-link
          `?sales_doc=…` doesn't match any OCR job in the current
          tenant. Almost always means the ocr_documents record was
          cleaned up while the sales_document.line_items survived.
          One-click re-scan brings the OCR job back. */}
      {salesDocOrphan && (
        <div className="hv-card p-3 border border-amber-500/40 bg-amber-500/10 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-amber-100">
              This sales document has no OCR job on file yet.
            </div>
            <div className="text-[11px] text-amber-100/70 mt-1 leading-snug">
              The extracted line items are still on the sales doc, but the OCR job record was cleaned up so per-line editing / SKU matching isn&apos;t available here. Trigger a fresh scan to re-populate this workspace.
            </div>
          </div>
          <button
            type="button"
            onClick={requestOrphanRescan}
            disabled={rescanBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-amber-400/50 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 text-[11px] font-medium disabled:opacity-50"
            data-testid="ocr-orphan-rescan-btn"
          >
            {rescanBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {rescanBusy ? "Re-scanning…" : "Re-scan this sales doc"}
          </button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        {[
          ["pending_review", "Pending review", "#F59E0B"],
          ["applied", "Applied", "#10B981"],
          ["processing", "Processing", "#38BDF8"],
          ["failed", "Failed", "#F43F5E"],
          ["rejected", "Rejected", "#F43F5E"],
          ["all", "All", "#647087"],
        ].map(([k, label, color]) => {
          const scoped = jobs.filter((j) =>
            (customerScope && presetCustomer) ? j.customer_id === presetCustomer : true,
          );
          const count = scoped.filter((j) => (k === "all" ? true : (j.status || "pending_review") === k)).length;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              data-testid={`ocr-tab-${k}`}
              className="px-3 py-1.5 rounded-full border text-xs uppercase tracking-wider"
              style={
                tab === k
                  ? { borderColor: color, background: `${color}1a`, color }
                  : { borderColor: "rgba(255,255,255,0.1)", color: "#a0aabf" }
              }
            >
              {label}{" "}
              <span className="ml-1 font-mono text-[10px] opacity-80">{count}</span>
            </button>
          );
        })}
        {presetCustomer && (
          <button
            type="button"
            onClick={() => setCustomerScope((v) => !v)}
            data-testid="ocr-scope-toggle"
            className="ml-auto px-3 py-1.5 rounded-full border text-xs uppercase tracking-wider"
            style={
              customerScope
                ? { borderColor: "#1FB6C1", background: "#1FB6C11a", color: "#1FB6C1" }
                : { borderColor: "rgba(255,255,255,0.1)", color: "#a0aabf" }
            }
            title="Toggle: show only this customer's OCR jobs vs all tenants' jobs"
          >
            {customerScope
              ? `Scoped: ${(customers.find((c) => c.id === presetCustomer)?.name || "customer").slice(0, 24)}`
              : "Show all customers"}
          </button>
        )}
      </div>

      {filtered.length === 0 && (
        <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="ocr-empty">
          <Sparkles className="w-6 h-6 text-[#1fb6c1]/60 mx-auto mb-2" />
          {jobs.length === 0
            ? "No invoices scanned yet. Tap 'Scan invoice' to capture one with your camera or upload a file."
            : (
              <div className="space-y-2">
                <div>No jobs in <b>{tab.replace("_", " ")}</b>{customerScope && presetCustomer ? " for this customer" : ""}.</div>
                <div className="text-xs">
                  Try switching to <button type="button" onClick={() => setTab("all")} className="underline text-cyan-300" data-testid="ocr-empty-all-btn">All</button>
                  {customerScope && presetCustomer && (
                    <> or <button type="button" onClick={() => setCustomerScope(false)} className="underline text-cyan-300" data-testid="ocr-empty-scope-btn">show all customers</button></>
                  )}.
                </div>
              </div>
            )}
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((j) => (
          <JobRow
            key={j.id}
            job={j}
            customers={customers}
            sites={sites}
            isOpen={openId === j.id}
            onToggle={() => setOpenId(openId === j.id ? null : j.id)}
            onChanged={load}
            highlight={
              (presetJob && j.id === presetJob) ||
              (presetSalesDoc && j.source_sales_document_id === presetSalesDoc)
            }
          />
        ))}
      </div>
    </div>
  );
}

function JobRow({ job, customers, sites, isOpen, onToggle, onChanged, highlight }) {
  // Iter 398c — row wrapper now stamps a deep-link testid used by the
  // scroll-into-view effect on the parent page, plus a highlight ring
  // when the row was targeted via `?job=` / `?sales_doc=`.
  const c = customers.find((x) => x.id === job.customer_id);
  const s = sites.find((x) => x.id === job.site_id);
  const status = job.status || "pending_review";
  const editable = status === "pending_review";
  const statusColor =
    {
      pending_review: "#F59E0B",
      applied: "#10B981",
      rejected: "#F43F5E",
      processing: "#38BDF8",
      failed: "#F43F5E",
    }[status] || "#647087";

  return (
    <div
      className={`hv-card overflow-hidden ${highlight ? "ring-2 ring-cyan-400/60" : ""}`}
      data-testid={`ocr-row-${job.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition text-left"
      >
        {isOpen ? <ChevronDown className="w-4 h-4 text-[#647087]" /> : <ChevronRight className="w-4 h-4 text-[#647087]" />}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">
            {job.supplier || "Unknown supplier"}
            {job.invoice_number && (
              <span className="ml-2 text-[#647087] font-mono text-xs">#{job.invoice_number}</span>
            )}
          </div>
          <div className="text-[11px] text-[#647087] mt-0.5">
            {(job.lines || []).length} line{(job.lines || []).length !== 1 ? "s" : ""}
            {c ? ` · ${c.name}` : " · no customer set"}
            {s ? ` → ${s.name}` : ""}
            {job.invoice_date ? ` · ${job.invoice_date}` : ""}
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border whitespace-nowrap"
          style={{ color: statusColor, borderColor: `${statusColor}55`, background: `${statusColor}1a` }}
        >
          {(job.status || "").replace("_", " ")}
        </span>
      </button>

      {isOpen && (
        <JobDetail
          job={job}
          customers={customers}
          sites={sites}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

function JobDetail({ job, customers, sites, onChanged }) {
  const [lines, setLines] = useState(job.lines || []);
  const [customerId, setCustomerId] = useState(job.customer_id || "");
  const [siteId, setSiteId] = useState(job.site_id || "");
  const [savingPatch, setSavingPatch] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const editable = job.status === "pending_review";

  const customerSites = sites.filter((s) => s.customer_id === customerId);

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const savePatch = async () => {
    setSavingPatch(true);
    try {
      await api.patch(`/ocr/invoice/${job.id}`, {
        customer_id: customerId,
        site_id: siteId,
        lines,
      });
      onChanged?.();
    } finally {
      setSavingPatch(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      // Save edits first.
      await api.patch(`/ocr/invoice/${job.id}`, { customer_id: customerId, site_id: siteId, lines });
      await api.post(`/ocr/invoice/${job.id}/apply`);
      onChanged?.();
    } finally {
      setApplying(false);
    }
  };

  const reject = async () => {
    await api.post(`/ocr/invoice/${job.id}/reject`, { reason: rejectReason });
    setConfirmReject(false);
    onChanged?.();
  };

  const linesWithEquipment = lines.filter((l) => l.will_create_equipment).length;
  const linesWithSchedule = lines.filter((l) => l.will_create_equipment && l.will_seed_schedule && l.interval_days).length;

  // 2026-02-18 Round A — invoice-level match summary so a 30-line invoice
  // doesn't hide a single bad SKU at the bottom. Banner is computed off the
  // same heuristic as `LineRow` (>10% price drift = price_mismatch).
  // Iter 290zp.13z14e — exclude `is_non_stock` lines (labour, travel,
  // project risk, deposits) from the no_match counter. They are EXPECTED
  // not to link to inventory; flagging them as "no match" was misleading.
  const matchSummary = lines.reduce((acc, l) => {
    if (l.is_non_stock) { acc.non_stock += 1; return acc; }
    const hasMatch = !!l.matched_inventory_id;
    const ocrPrice = Number(l.unit_price ?? 0);
    const invPrice = Number(l.matched_unit_price ?? 0);
    const drift = hasMatch && invPrice > 0 && ocrPrice > 0
      ? Math.abs(ocrPrice - invPrice) / invPrice
      : 0;
    if (!hasMatch) acc.no_match += 1;
    else if (drift > 0.10) acc.price_drift += 1;
    else acc.matched += 1;
    return acc;
  }, { matched: 0, price_drift: 0, no_match: 0, non_stock: 0 });

  // Iter 290zp.13z14e — totals reconciliation. We sum every line_total
  // (including labour/travel/non-stock) and compare against the document's
  // own printed total. Anything more than R1 apart surfaces an amber
  // "doesn't reconcile" banner. Per product spec (#1) this WARNS but does
  // NOT block Apply — the user remains in control. They can edit the
  // quantity/unit_price/line_total cells to reconcile, hit Save edits,
  // then Apply.
  const linesSum = lines.reduce((s, l) => s + Number(l.line_total ?? 0), 0);
  const docSubtotal = Number(job.totals?.subtotal ?? 0);
  const docTotal = Number(job.totals?.total ?? 0);
  // Prefer subtotal (excl VAT) since that's what line_totals sum to. Fall
  // back to grand total if subtotal isn't printed.
  const docRef = docSubtotal > 0 ? docSubtotal : docTotal;
  const reconcileDelta = docRef > 0 ? (linesSum - docRef) : 0;
  // Iter 393b — Only reconcile when there ARE line items. A statement
  // (or any doc where OCR extracted 0 lines) has nothing to reconcile
  // against — showing the amber "line totals R0 is short of R307,229"
  // banner just confused users (Dirk). Skip the warning when lines are
  // empty. Also skip when the document category is `statement`.
  const isStatementLike = (job.category === "statement")
    || (job.doc_kind === "statement")
    || (lines.length === 0);
  const reconcileOk = isStatementLike || !(docRef > 0) || Math.abs(reconcileDelta) <= 1.00;

  return (
    <div className="border-t border-white/5 p-4 space-y-4" data-testid={`ocr-job-detail-${job.id}`}>
      {/* Customer + site */}
      {editable ? (
        <>
          {(job.bill_to?.name || job.matched_customer_name) && (
            <div className="rounded-md border border-cyan-500/30 bg-cyan-500/[0.05] p-2.5 text-xs space-y-0.5" data-testid={`ocr-billto-${job.id}`}>
              <div className="flex items-center gap-1.5 text-cyan-300 uppercase tracking-wider text-[10px] font-bold">
                <Sparkles className="w-3 h-3" /> Bill-to extracted from invoice
              </div>
              {job.bill_to?.name && (
                <div className="text-[#dde3ec]">
                  <span className="text-[#647087]">Name:</span> {job.bill_to.name}
                  {job.bill_to.account_ref && <span className="ml-2 font-mono text-[#647087]">#{job.bill_to.account_ref}</span>}
                  {job.bill_to.vat_number && <span className="ml-2 font-mono text-[#647087]">VAT {job.bill_to.vat_number}</span>}
                </div>
              )}
              {job.matched_customer_name && (
                <div className="text-emerald-300">
                  ✓ Matched <b>{job.matched_customer_name}</b>{" "}
                  <span className="opacity-60 font-mono text-[10px]">
                    ({Math.round((job.matched_customer_confidence || 0) * 100)}% confidence)
                  </span>
                </div>
              )}
              {job.bill_to?.name && !job.matched_customer_name && (
                <div className="text-amber-300">
                  No customer match — pick one below or create a new customer first.
                </div>
              )}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Customer *</label>
              <SearchableSelect
                value={customerId || ""}
                onChange={(id) => { setCustomerId(id); setSiteId(""); }}
                options={customers.map((c) => ({
                  id: c.id,
                  label: c.name,
                  sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.vat_number && `VAT ${c.vat_number}`, c.phone, c.email].filter(Boolean).join(" · "),
                  searchTerms: [c.name, c.external_ref, c.vat_number, c.phone, c.whatsapp, c.email, c.industry, c.physical_address, c.legal_name, c.short_id].filter(Boolean),
                }))}
                placeholder="— Pick customer —"
                required
                testId={`ocr-detail-customer-${job.id}`}
                emptyHint="No customers match. Try VAT, account no., phone or email."
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Site *</label>
              <SearchableSelect
                value={siteId || ""}
                onChange={setSiteId}
                options={customerSites.map((s) => ({
                  id: s.id,
                  label: s.name,
                  sublabel: s.address || "",
                  searchTerms: [s.name, s.address, s.external_ref].filter(Boolean),
                }))}
                placeholder={customerId ? "— Pick site —" : "Pick customer first"}
                required
                disabled={!customerId}
                testId={`ocr-detail-site-${job.id}`}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="text-xs text-[#647087]">
          {customers.find((c) => c.id === job.customer_id)?.name || "—"}
          {" · "}
          {sites.find((s) => s.id === job.site_id)?.name || "—"}
        </div>
      )}

      {/* Lines table */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
            Line items ({lines.length})
          </div>
          {lines.length > 0 && (
            <div
              className="flex items-center gap-1.5 text-[10px]"
              data-testid={`ocr-match-summary-${job.id}`}
            >
              {matchSummary.matched > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                  ✓ {matchSummary.matched} matched
                </span>
              )}
              {matchSummary.price_drift > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30"
                  data-testid={`ocr-match-summary-pricedrift-${job.id}`}
                >
                  ⚠ {matchSummary.price_drift} price drift
                </span>
              )}
              {matchSummary.no_match > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full bg-rose-500/10 text-rose-300 border border-rose-500/30"
                  data-testid={`ocr-match-summary-nomatch-${job.id}`}
                >
                  ✕ {matchSummary.no_match} no match
                </span>
              )}
            </div>
          )}
        </div>
        {!reconcileOk && editable && (
          <div
            className="rounded-md border border-amber-500/40 bg-amber-500/[0.06] p-2.5 text-[11px] text-amber-200/95 flex items-start gap-2"
            data-testid={`ocr-totals-mismatch-${job.id}`}
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <b>Totals don&apos;t reconcile.</b> Sum of line totals
              {" "}<span className="font-mono">R{linesSum.toFixed(2)}</span>{" "}
              {reconcileDelta > 0 ? "exceeds" : "is short of"}{" "}
              the document {docSubtotal > 0 ? "subtotal" : "total"}
              {" "}<span className="font-mono">R{docRef.toFixed(2)}</span>{" "}
              by <span className="font-mono font-bold">R{Math.abs(reconcileDelta).toFixed(2)}</span>.
              {" "}Review the line items below; you can edit quantity, unit price or line total cells inline. Save edits, then Apply.
            </div>
          </div>
        )}
        {(matchSummary.no_match > 0 || matchSummary.price_drift > 0) && editable && (
          <div
            className="rounded-md border border-amber-500/30 bg-amber-500/[0.05] p-2.5 text-[11px] text-amber-200/90 flex items-start gap-2"
            data-testid={`ocr-match-banner-${job.id}`}
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              {matchSummary.no_match > 0 && (
                <>This invoice has <b>{matchSummary.no_match}</b> line{matchSummary.no_match !== 1 ? "s" : ""} with no inventory match. </>
              )}
              {matchSummary.price_drift > 0 && (
                <>{matchSummary.no_match > 0 ? "Also, " : ""}<b>{matchSummary.price_drift}</b> line{matchSummary.price_drift !== 1 ? "s" : ""} differ from inventory price by &gt;10%. </>
              )}
              Review the highlighted rows below before applying.
            </div>
          </div>
        )}
        {lines.length === 0 && (
          <div className="text-xs text-[#647087] italic px-2 py-3 border border-white/5 rounded-md text-center">
            No lines extracted.
            {job.model_error && <div className="mt-1 text-rose-300">Error: {job.model_error}</div>}
          </div>
        )}
        {lines.map((ln, idx) => (
          <LineRow
            key={idx}
            line={ln}
            idx={idx}
            editable={editable}
            onChange={(patch) => updateLine(idx, patch)}
            jobId={job.id}
          />
        ))}
      </div>

      {/* Footer summary + actions */}
      {editable && (
        <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/5 flex-wrap">
          <div className="text-[11px] text-[#647087]">
            Will create <span className="text-cyan-300 font-bold">{linesWithEquipment}</span> equipment row{linesWithEquipment !== 1 ? "s" : ""},{" "}
            seed <span className="text-emerald-300 font-bold">{linesWithSchedule}</span> service schedule{linesWithSchedule !== 1 ? "s" : ""}.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmReject(true)}
              className="hv-btn hv-btn-ghost text-rose-300"
              data-testid={`ocr-reject-${job.id}`}
            >
              <X className="w-4 h-4" /> Reject
            </button>
            <button
              type="button"
              onClick={savePatch}
              disabled={savingPatch || applying}
              className="hv-btn hv-btn-ghost"
              data-testid={`ocr-save-${job.id}`}
            >
              <Save className="w-4 h-4" /> {savingPatch ? "Saving…" : "Save edits"}
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={applying || !customerId || !siteId || linesWithEquipment === 0}
              className="hv-btn hv-btn-primary"
              data-testid={`ocr-apply-${job.id}`}
              title={!customerId || !siteId ? "Pick customer + site first" : "Create equipment & seed schedules"}
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {applying ? "Applying…" : `Apply (${linesWithEquipment})`}
            </button>
          </div>
        </div>
      )}

      {!editable && job.status === "applied" && (
        <div className="pt-3 border-t border-white/5 text-[11px] text-[#647087]">
          Applied {job.applied_at ? new Date(job.applied_at).toLocaleString('en-GB') : ""} by {job.applied_by_name}.{" "}
          {(job.applied_equipment_ids || []).length} equipment row{(job.applied_equipment_ids || []).length !== 1 ? "s" : ""} created.{" "}
          {customerId && (
            <Link to={`/customers/${customerId}`} className="text-cyan-300 hover:underline">
              View customer →
            </Link>
          )}
        </div>
      )}

      {!editable && job.status === "rejected" && (
        <div className="pt-3 border-t border-white/5 text-[11px] text-rose-300">
          Rejected by {job.rejected_by_name} {job.rejected_at ? new Date(job.rejected_at).toLocaleString('en-GB') : ""}
          {job.reject_reason && <> — {job.reject_reason}</>}
        </div>
      )}

      <ConfirmModal
        open={confirmReject}
        onClose={() => setConfirmReject(false)}
        onConfirm={reject}
        title="Reject this OCR job?"
        message={
          <div className="space-y-2">
            <p>Mark this scan as rejected. The image stays for audit, but no equipment will be created.</p>
            <input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (optional)"
              className="hv-input text-sm w-full"
              data-testid={`ocr-reject-reason-${job.id}`}
            />
          </div>
        }
        confirmLabel="Reject job"
        testId="ocr-reject-confirm"
      />
    </div>
  );
}

function LineRow({ line, idx, editable, onChange, jobId }) {
  // 2026-02-18 Round A — mismatch chips.
  // The match engine sets `matched_inventory_id` + `matched_unit_price`
  // when it found a SKU; absence ⇒ no inventory match.
  // Compare OCR unit_price vs inventory unit_price; > 10% drift = amber.
  // Iter 290zp.13z14e — `is_non_stock` lines (labour, travel, project risk,
  // deposits) suppress the rose "no match" border entirely; they aren't
  // expected to map to the inventory catalogue and showing the warning was
  // noisy / misleading. They're rendered with a neutral "non-stock" chip
  // and remain fully save-able / apply-able.
  const isNonStock = !!line.is_non_stock;
  const hasMatch = !!line.matched_inventory_id;
  const ocrPrice = Number(line.unit_price ?? 0);
  const invPrice = Number(line.matched_unit_price ?? 0);
  const priceDriftPct = hasMatch && invPrice > 0 && ocrPrice > 0
    ? Math.abs(ocrPrice - invPrice) / invPrice
    : 0;
  const isMismatchPrice = priceDriftPct > 0.10;
  const matchState = isNonStock
    ? "non_stock"
    : !hasMatch
      ? "no_match"
      : isMismatchPrice
        ? "mismatch_price"
        : "matched";

  // Iter 290zp.13z14e — inline inventory re-link picker (Change C).
  // When `linking` is true we render a search input that hits the
  // inventory list endpoint and lists candidates as a dropdown directly
  // under the line. Picking one stamps the line's matched_* fields and
  // closes the picker. We keep the picker LOCAL state because mounting
  // a global popover triggers excessive re-renders on lists with 20+
  // lines.
  const [linking, setLinking] = useState(false);
  const [linkQuery, setLinkQuery] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  // Iter 427.p1 — After a manual pick, ask if the OCR line's
  // name + SKU should be aligned to the chosen inventory. Prevents
  // downstream drift ("SO says X but financials say Y") when the OCR
  // vision text disagrees with the master catalogue.
  const [alignPrompt, setAlignPrompt] = useState(null);

  useEffect(() => {
    if (!linking) return;
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      setLinkLoading(true);
      try {
        const { data } = await api.get("/inventory", {
          params: { q: linkQuery || line.sku || line.name || "", limit: 12 },
          signal: ctl.signal,
        });
        const arr = Array.isArray(data) ? data : (data?.items || []);
        setLinkResults(arr.slice(0, 12));
      } catch (e) {
        if (e?.name !== "CanceledError") setLinkResults([]);
      } finally {
        setLinkLoading(false);
      }
    }, 220);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [linking, linkQuery, line.sku, line.name]);

  const pickInventory = (it) => {
    onChange({
      matched_inventory_id: it.id,
      matched_inventory_name: it.name,
      matched_unit_price: it.unit_price ?? null,
      matched_confidence: 1.0,
      match_reason: "manual",
      matched_sku: it.sku || null,
    });
    setLinking(false);
    setLinkQuery("");
    // Ask if the OCR text on this line should be aligned to inventory.
    // Only prompt when there's a real difference to fix.
    const invName = (it.name || "").trim();
    const invSku = (it.sku || "").trim();
    const curName = ((line.name || line.raw_text) || "").trim();
    const curSku = (line.sku || "").trim();
    if (invName && (invName.toLowerCase() !== curName.toLowerCase()
                    || (invSku && invSku.toLowerCase() !== curSku.toLowerCase()))) {
      setAlignPrompt({
        invId: it.id,
        invName,
        invSku,
        curName,
        curSku,
      });
    }
  };

  const applyAlignment = () => {
    if (!alignPrompt) return;
    onChange({
      name: alignPrompt.invName,
      sku: alignPrompt.invSku || null,
    });
    setAlignPrompt(null);
  };

  const unlinkInventory = () => {
    onChange({
      matched_inventory_id: null,
      matched_inventory_name: null,
      matched_unit_price: null,
      matched_confidence: 0,
      match_reason: "manual_unlink",
    });
  };

  return (
    <div
      className={
        "grid grid-cols-12 gap-2 items-center px-2 py-2 rounded-md border bg-white/[0.02] text-xs " + (
          matchState === "no_match"
            ? "border-rose-500/40"
            : matchState === "mismatch_price"
              ? "border-amber-500/40"
              : matchState === "non_stock"
                ? "border-cyan-500/20"
                : "border-white/5"
        )
      }
      data-testid={`ocr-line-${jobId}-${idx}`}
    >
      <div className="col-span-12 md:col-span-5 min-w-0">
        {editable ? (
          <input
            value={line.name || ""}
            onChange={(e) => onChange({ name: e.target.value })}
            className="hv-input py-1 px-2 text-xs w-full"
            data-testid={`ocr-line-name-${idx}`}
          />
        ) : (
          <div className="font-medium truncate">{line.name}</div>
        )}
        {line.raw_text && line.raw_text !== line.name && (
          <div className="text-[10px] text-[#647087] truncate mt-0.5" title={line.raw_text}>
            {line.raw_text}
          </div>
        )}
        {/* Match-state chip — replaces the old "matched inventory" line so
            no-match and mismatch_price states are visible at a glance. */}
        {matchState === "non_stock" && (
          <div
            className="text-[10px] text-cyan-300 mt-0.5 flex items-center gap-1"
            data-testid={`ocr-line-nonstock-${idx}`}
          >
            <Sparkles className="w-3 h-3" />
            <span>Non-stock line (labour / travel / service) — saved as-is.</span>
          </div>
        )}
        {matchState === "matched" && line.matched_inventory_name && (
          <div
            className="text-[10px] text-emerald-300 mt-0.5 flex items-center gap-1"
            title={`Matched at ${Math.round(((line.matched_confidence || 0) * 100))}% confidence`}
            data-testid={`ocr-line-match-ok-${idx}`}
          >
            <Sparkles className="w-3 h-3" />
            <span className="truncate">↔ {line.matched_inventory_name}</span>
            <span className="font-mono opacity-60">({Math.round(((line.matched_confidence || 0) * 100))}%)</span>
            {editable && (
              <button
                type="button"
                onClick={() => setLinking((v) => !v)}
                className="text-cyan-300 hover:text-cyan-200 underline"
                data-testid={`ocr-line-relink-${idx}`}
              >
                Re-link
              </button>
            )}
          </div>
        )}
        {matchState === "mismatch_price" && line.matched_inventory_name && (
          <div
            className="text-[10px] text-amber-300 mt-0.5 flex items-center gap-1"
            title={`Inventory price R${invPrice.toFixed(2)} vs OCR price R${ocrPrice.toFixed(2)} (${Math.round(priceDriftPct * 100)}% drift)`}
            data-testid={`ocr-line-match-pricemismatch-${idx}`}
          >
            <AlertTriangle className="w-3 h-3" />
            <span className="truncate">↔ {line.matched_inventory_name}</span>
            <span className="font-mono opacity-80">price drift {Math.round(priceDriftPct * 100)}%</span>
            {editable && (
              <button
                type="button"
                onClick={() => setLinking((v) => !v)}
                className="text-cyan-300 hover:text-cyan-200 underline ml-1"
                data-testid={`ocr-line-relink-${idx}`}
              >
                Re-link
              </button>
            )}
          </div>
        )}
        {matchState === "no_match" && (
          <div
            className="text-[10px] text-rose-300 mt-0.5 flex items-center gap-1 flex-wrap"
            data-testid={`ocr-line-match-none-${idx}`}
          >
            <AlertTriangle className="w-3 h-3" />
            <span>No inventory match.</span>
            {editable && (
              <button
                type="button"
                onClick={() => setLinking((v) => !v)}
                className="text-cyan-300 hover:text-cyan-200 underline"
                data-testid={`ocr-line-link-${idx}`}
              >
                Link to inventory
              </button>
            )}
            {editable && (
              <span className="text-[#647087]">or it will be created on Apply.</span>
            )}
          </div>
        )}
        {/* Inline inventory picker — Iter 290zp.13z14e */}
        {editable && linking && (
          <div
            className="mt-1.5 rounded-md border border-cyan-500/30 bg-cyan-500/[0.04] p-1.5 space-y-1"
            data-testid={`ocr-line-linker-${idx}`}
          >
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={linkQuery}
                onChange={(e) => setLinkQuery(e.target.value)}
                placeholder={`Search inventory… (default: ${line.sku || line.name || ""})`}
                className="hv-input py-1 px-2 text-[11px] w-full"
                autoFocus
                data-testid={`ocr-line-linker-input-${idx}`}
              />
              {linkLoading && <Loader2 className="w-3 h-3 animate-spin text-[#647087] shrink-0" />}
              <button
                type="button"
                onClick={() => { setLinking(false); setLinkQuery(""); }}
                className="text-[10px] text-[#647087] hover:text-white"
                data-testid={`ocr-line-linker-close-${idx}`}
              >
                ✕
              </button>
            </div>
            {hasMatch && (
              <button
                type="button"
                onClick={unlinkInventory}
                className="text-[10px] text-rose-300 hover:text-rose-200 underline"
                data-testid={`ocr-line-unlink-${idx}`}
              >
                Unlink current match
              </button>
            )}
            <div className="max-h-44 overflow-y-auto">
              {linkResults.length === 0 && !linkLoading && (
                <div className="text-[10px] text-[#647087] italic py-1 px-1">
                  {linkQuery ? "No matches." : "Type to search…"}
                </div>
              )}
              {linkResults.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => pickInventory(r)}
                  className="w-full text-left text-[11px] p-1.5 rounded hover:bg-white/[0.06] flex items-center gap-2"
                  data-testid={`ocr-line-linker-pick-${idx}-${r.id}`}
                >
                  <span className="font-mono text-cyan-300 shrink-0">{r.sku || "—"}</span>
                  <span className="flex-1 truncate">{r.name}</span>
                  {r.on_hand != null && (
                    <span className="text-[10px] text-[#647087] font-mono shrink-0">on-hand {r.on_hand}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Iter 427.p1 — Align OCR text to inventory prompt.
            Fires immediately after `pickInventory` when the chosen
            item's name/SKU differs from what OCR extracted. Prevents
            "SO says etatron dosing pump but financials say utility
            brush" drift. */}
        {alignPrompt && (
          <div
            className="mt-1.5 rounded-md border border-amber-400/40 bg-amber-400/[0.08] p-1.5 space-y-1"
            data-testid={`ocr-line-align-prompt-${idx}`}
          >
            <div className="text-[10px] text-amber-200 font-medium flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Align this line to inventory?
            </div>
            <div className="text-[10px] text-[#a0aabf] leading-snug space-y-0.5">
              <div>
                <span className="text-[#647087]">OCR reads:</span>{" "}
                <span className="text-white">{alignPrompt.curName || "—"}</span>
                {alignPrompt.curSku && (
                  <span className="font-mono text-[#647087] ml-1">· SKU {alignPrompt.curSku}</span>
                )}
              </div>
              <div>
                <span className="text-[#647087]">Inventory:</span>{" "}
                <span className="text-emerald-300">{alignPrompt.invName}</span>
                {alignPrompt.invSku && (
                  <span className="font-mono text-[#647087] ml-1">· SKU {alignPrompt.invSku}</span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              <button
                type="button"
                onClick={applyAlignment}
                className="text-[10px] px-2 py-0.5 rounded border border-emerald-400/40 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/20"
                data-testid={`ocr-line-align-yes-${idx}`}
              >
                Rename + set SKU
              </button>
              <button
                type="button"
                onClick={() => setAlignPrompt(null)}
                className="text-[10px] px-2 py-0.5 rounded border border-white/15 text-[#a0aabf] hover:border-white/30"
                data-testid={`ocr-line-align-no-${idx}`}
              >
                Keep OCR text
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="col-span-3 md:col-span-1">
        {editable ? (
          <input
            type="number"
            min={0}
            step="0.01"
            value={line.quantity ?? 1}
            onChange={(e) => onChange({ quantity: Number(e.target.value) })}
            className="hv-input py-1 px-2 text-xs"
            data-testid={`ocr-line-qty-${idx}`}
          />
        ) : (
          <div className="text-[#a0aabf]">×{line.quantity || 1}</div>
        )}
      </div>

      <div className="col-span-3 md:col-span-1 text-[10px] text-[#647087] truncate">
        {line.unit || "pcs"}
      </div>

      <div className="col-span-6 md:col-span-2">
        {editable ? (
          <label className="flex items-center gap-1 text-[10px] cursor-pointer">
            <input
              type="checkbox"
              checked={!!line.is_service_item}
              onChange={(e) => onChange({ is_service_item: e.target.checked })}
              data-testid={`ocr-line-service-${idx}`}
            />
            Service item
          </label>
        ) : (
          line.is_service_item && <span className="text-emerald-300">⟳ recurring</span>
        )}
        {editable && line.is_service_item && (
          <div className="flex items-center gap-1 mt-1">
            <input
              type="number"
              min={1}
              max={3650}
              value={line.interval_days ?? ""}
              onChange={(e) =>
                onChange({
                  interval_days: e.target.value ? Number(e.target.value) : null,
                  will_seed_schedule: !!e.target.value,
                })
              }
              placeholder="days"
              className="hv-input py-0.5 px-1 text-[10px] w-16"
              data-testid={`ocr-line-interval-${idx}`}
            />
            <span className="text-[9px] text-[#647087]">d</span>
          </div>
        )}
      </div>

      <div className="col-span-12 md:col-span-3 flex items-center justify-end gap-2">
        {editable ? (
          <>
            <label className="flex items-center gap-1 text-[10px] text-[#a0aabf] cursor-pointer">
              <input
                type="checkbox"
                checked={!!line.will_create_equipment}
                onChange={(e) => onChange({ will_create_equipment: e.target.checked })}
                data-testid={`ocr-line-include-${idx}`}
              />
              Include
            </label>
            <button
              type="button"
              onClick={() => onChange({ will_create_equipment: false })}
              className="text-[#647087] hover:text-rose-300 p-0.5"
              title="Skip this line"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <span className="text-[10px] text-[#647087]">
            {line.unit_price ? `${line.unit_price.toFixed?.(2) ?? line.unit_price}` : ""}
          </span>
        )}
      </div>
    </div>
  );
}
